import type { JSX } from 'solid-js'
import { createEffect, createSignal, For, Index, onCleanup, Show } from 'solid-js'
import { twMerge } from 'tailwind-merge'
import Icon from '~/components/Icon'
import type { TabsPropsType } from '~/types/Tabs'

export default function Tabs(props: TabsPropsType): JSX.Element {
  const [activeTab, setActiveTab] = createSignal(props.activeTab || (props.items.length > 0 ? props.items[0].id : ''))
  const [isTransitioning, setIsTransitioning] = createSignal(false)
  // keepMounted: tabs cuyo contenido ya se montó. El primer tab monta al
  // instante; el resto se monta diferido (fuera del clic) y nunca se desmonta.
  const [mountedTabs, setMountedTabs] = createSignal<Set<string>>(new Set(props.keepMounted && props.showContent && props.items.length > 0 ? [props.items[0].id] : []))

  createEffect(() => {
    if (props.activeTab) {
      setActiveTab(props.activeTab)
    }
  })

  createEffect(() => {
    if (!props.keepMounted || !props.showContent) return
    const id = activeTab()
    if (mountedTabs().has(id) || !activeItem()?.content) return
    // El montaje del contenido pesado sale del clic: se agenda y se pinta en
    // el siguiente task.
    const timer = setTimeout(() => {
      setMountedTabs(prev => new Set(prev).add(id))
    }, 0)
    onCleanup(() => clearTimeout(timer))
  })

  const handleTabClick = (tabId: string) => {
    if (activeTab() !== tabId) {
      // El contenido se cambia al instante. 'fade' solo hace un fade-in del
      // contenido nuevo (montado con keyed, la animación corre al montar);
      // 'slide' conserva su transición de entrada. Nada de fade-out ni hueco:
      // antes se veía un blip (contenido → se apaga → reaparece).
      if (props.transition === 'slide') {
        setIsTransitioning(true)
        setTimeout(() => {
          setActiveTab(tabId)
          setIsTransitioning(false)
        }, 100)
      } else {
        setActiveTab(tabId)
      }
    }
    props.onChange?.(tabId)
  }

  const getTabStyles = (isActive: boolean, isDisabled: boolean, variant: string = 'default') => {
    const baseStyles = 'inline-flex items-center justify-center transition-all duration-150 ease-out'

    switch (variant) {
      case 'pills':
        return twMerge(
          baseStyles,
          'px-4 py-2 rounded-full font-medium',
          isActive ? 'bg-primary-500 text-white shadow-md' : 'bg-gray-100 text-foreground-muted hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700',
          isDisabled && 'opacity-50 cursor-not-allowed'
        )

      case 'underline':
        return twMerge(
          baseStyles,
          'px-4 py-2 border-b-2 font-medium',
          isActive ? 'border-primary-500 text-primary' : 'border-transparent text-foreground-subtle hover:text-gray-700 hover:border-gray-300 dark:hover:text-gray-300',
          isDisabled && 'opacity-50 cursor-not-allowed'
        )

      case 'buttons':
        return twMerge(
          baseStyles,
          'px-6 py-3 rounded-lg font-medium border',
          isActive ? 'bg-primary-500 text-white border-primary-500 shadow-md' : 'bg-surface text-foreground-muted border-border-strong hover:bg-surface-hover',
          isDisabled && 'opacity-50 cursor-not-allowed'
        )

      default:
        return twMerge(
          baseStyles,
          'px-4 py-3 border-b-2 font-medium',
          isActive ? 'text-primary border-primary' : 'border-transparent text-foreground-subtle hover:text-gray-600 hover:border-gray-300 dark:hover:text-gray-300',
          isDisabled && 'opacity-50 cursor-not-allowed'
        )
    }
  }

  const getTabsContainerStyles = (variant: string = 'default', size: string = 'md', fullWidth: boolean = false) => {
    const sizeStyles = {
      xs: 'text-xs',
      sm: 'text-sm',
      md: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl'
    }

    return twMerge(
      'flex',
      fullWidth ? 'w-full' : 'whitespace-nowrap',
      sizeStyles[size as keyof typeof sizeStyles] || sizeStyles.md,
      variant === 'pills' ? 'gap-2 p-1 bg-surface-muted rounded-full' : '',
      variant === 'buttons' ? 'gap-2' : '',
      variant === 'default' || variant === 'underline' ? '-mb-px' : '',
      fullWidth ? '' : 'overflow-x-auto hide-scrollbar'
    )
  }

  const getContentStyles = (transition: string = 'fade') => {
    const baseStyles = 'transition-all duration-200 ease-out'

    if (transition === 'slide') {
      return twMerge(baseStyles, isTransitioning() ? 'transform translate-x-4 opacity-0' : 'transform translate-x-0 opacity-100')
    } else if (transition === 'fade') {
      return twMerge(baseStyles, isTransitioning() ? 'opacity-0' : 'opacity-100')
    }

    return baseStyles
  }

  const activeItem = () => props.items.find(item => item.id === activeTab())

  return (
    <div class={twMerge('w-full', props.class || '')}>
      <Show when={activeItem()?.title}>
        <div class="mb-4 p-4 bg-surface-muted rounded-lg">
          <div class="flex items-center gap-3 mb-2">
            <Show when={activeItem()?.icon}>
              <Icon icon={activeItem()?.icon!} size={24} class="text-primary-500" />
            </Show>
            <div>
              <h3 class="text-lg font-semibold text-foreground">{activeItem()?.title}</h3>
              <Show when={activeItem()?.subtitle}>
                <p class="text-sm text-foreground-subtle">{activeItem()?.subtitle}</p>
              </Show>
            </div>
          </div>
        </div>
      </Show>

      <div class={twMerge('border-b border-border mb-2', props.variant === 'pills' || props.variant === 'buttons' ? 'border-0' : '')}>
        <ul class={getTabsContainerStyles(props.variant, props.size, props.fullWidth)}>
          {/* Index y no For: al cambiar una etiqueta o un contador, For recrearía
              el botón y su icono; con Index sólo se actualiza lo que cambió. */}
          <Index each={props.items}>
            {item => (
              <li class={twMerge(props.fullWidth ? 'flex-1' : '')}>
                <button
                  // Sin esto, un Tabs dentro de un <form> envía el formulario al
                  // cambiar de pestaña: un <button> sin `type` es de tipo submit.
                  type="button"
                  onClick={() => !item().disabled && handleTabClick(item().id)}
                  class={getTabStyles(activeTab() === item().id, !!item().disabled, props.variant)}
                  disabled={item().disabled}
                  aria-current={activeTab() === item().id ? 'page' : undefined}
                >
                  <Show when={item().icon}>
                    <Icon icon={item().icon!} size={props.size === 'xs' ? 16 : props.size === 'sm' ? 18 : props.size === 'lg' ? 24 : props.size === 'xl' ? 28 : 22} class="mr-2" />
                  </Show>
                  <span>{item().label}</span>
                  <Show when={item().badge}>
                    <span class="ml-2 inline-flex items-center justify-center rounded-full bg-primary-500 px-1.5 text-xs font-medium text-white leading-none h-5">{item().badge}</span>
                  </Show>
                </button>
              </li>
            )}
          </Index>
        </ul>
      </div>

      <Show when={props.showContent}>
        <Show
          when={props.keepMounted}
          fallback={
            <Show when={activeItem()?.content && activeTab()} keyed>
              <div class={props.transition === 'slide' ? getContentStyles('slide') : 'animate-in fade-in duration-200'}>{activeItem()?.content}</div>
            </Show>
          }
        >
          {/* keepMounted: el contenido montado se queda en el DOM; solo se
              alterna el visible. Cambiar de tab no desmonta ni remonta nada. */}
          <For each={props.items}>
            {item => (
              <Show when={mountedTabs().has(item.id)}>
                <div hidden={activeTab() !== item.id} class="animate-in fade-in duration-200">
                  {item.content}
                </div>
              </Show>
            )}
          </For>
        </Show>
      </Show>
    </div>
  )
}
